import { Injectable } from '@angular/core';
import { LocationService } from '@core/services/location.service';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { I18nService } from '@yourcause/common/i18n';
import { ModalFactory } from '@yourcause/common/modals';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { PageHelpModalComponent } from './page-help-modal/page-help-modal.component';
import { PageHelpModalResources } from './page-help-modals.resources';
import { PageHelpModalState } from './page-help-modals.state';
import { PageHelpMap, PageHelpRoutes } from './page-help-modals.typing';

const ApplicationsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-applications-grant-managers.html';
const ApplicationsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-grant-managers-applications.html';

const NominationsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-nominations-grant-managers.html';
const NominationsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-grant-managers-nominations.html';

const ReportingHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-ad-hoc-reports.html';
const ReportingHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-ad-hoc-reports.html';

const PaymentProcessingHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-payments.html';
const PaymentProcessingHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-payments.html';

const GrantProgramsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-grant-programs.html';
const GrantProgramsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-grant-programs.html';

const NominationProgramsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-nomination-programs.html';
const NominationProgramsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-nomination-programs.html';

const BudgetsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-budgets.html';
const BudgetsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-budgets.html';

const CustomFormsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-custom-forms.html';
const CustomFormsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-forms.html';

const FundingSourcesHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-funding-sources.html';
const FundingSourcesHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-funding.html';

const WorkflowsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-workflows.html';
const WorkflowsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-workflows.html';

const SystemEmailsHelpLink1 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-guide-system-emails.html';
const SystemEmailsHelpLink2 = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-system-emails.html';

@AttachYCState(PageHelpModalState)
@Injectable({ providedIn: 'root' })
export class PageHelpModalService extends BaseYCService<PageHelpModalState> {
  pageHelpRoutes = [
    PageHelpRoutes.APPLICATIONS,
    PageHelpRoutes.NOMINATIONS,
    PageHelpRoutes.REPORTING,
    PageHelpRoutes.PAYMENT_PROCESSING,
    PageHelpRoutes.SYSTEM_EMAILS,
    PageHelpRoutes.PROGRAM_SETUP_GRANT_PROGRAMS,
    PageHelpRoutes.PROGRAM_SETUP_CUSTOM_FORMS,
    PageHelpRoutes.PROGRAM_SETUP_NOMINATION_PROGRAMS,
    PageHelpRoutes.PROGRAM_SETUP_BUDGETS,
    PageHelpRoutes.PROGRAM_SETUP_FUNDING_SOURCES,
    PageHelpRoutes.PROGRAM_SETUP_WORKFLOWS
  ];

  keyFeaturesText = this.i18n.translate('common:hdrKeyFeatures', {}, 'Key Features');
  learnMoreText = this.i18n.translate('common:hdrLearnMore', {}, 'Learn More');
  viewAllResources = this.i18n.translate('common:textViewAllResources', {}, 'View all resources');
  findOutMoreText = this.i18n.translate(
    'common:textFindOutMoreInHelpCenter',
    {},
    'Find out more about this feature in our Help Center.'
  );

  constructor (
    private pageHelpModalResources: PageHelpModalResources,
    private i18n: I18nService,
    private modalFactory: ModalFactory,
    private clientSettingsService: ClientSettingsService,
    private locationService: LocationService
  ) {
    super();
  }

  get pageHelpDismissedMap () {
    return this.get('pageHelpDismissedMap');
  }

  /**
   * Sets the Page Help Map
   * The left side of this map is the route, and the right side is a boolean if it's been dismissed
   */
  async setPageHelpInfo () {
    if (!this.pageHelpDismissedMap) {
      const dismissedPageHelp = await this.pageHelpModalResources.getCompletedPageHelp();
      const map = dismissedPageHelp.reduce<PageHelpMap>((acc, item) => {
        return {
          ...acc,
          [item.tourKey]: !!item.dateCompleted
        };
      }, {} as PageHelpMap);
      this.set('pageHelpDismissedMap', map);
    }
  }

  /**
   * Based on the current route,
   * determine if we need to open the page help modal
   */
  checkIfWeShouldOpenPageHelpModal () {
    if (!this.clientSettingsService.isBBGM) {
      const genericRoute = this.locationService.getGenericRoute() as PageHelpRoutes;
      if (this.shouldShowModal(genericRoute)) {
        this.openModal(genericRoute);
      }
    }
  }

  /**
   * Should we show the page help modal?
   *
   * @param route: Route to check
   * @returns if we should show the page help modal
   */
  shouldShowModal (route: PageHelpRoutes) {
    const isHelpRoute = this.pageHelpRoutes.includes(route);
    if (isHelpRoute) {
      const hasAlreadySeen = !!sessionStorage.getItem(route);
      const hasNotBeenDismissed = !this.pageHelpDismissedMap[route];

      return hasNotBeenDismissed && !hasAlreadySeen;
    }

    return false;
  }

  /**
   * Open the Page Help Modal
   *
   * @param route: Page route
   */
  async openModal (route: PageHelpRoutes) {
    sessionStorage.setItem(route, 'true');
    const dismiss = await this.modalFactory.open(
      PageHelpModalComponent,
      {
        route
      }
    );
    if (dismiss) {
      await this.pageHelpModalResources.dismissPageHelp(route);
    }
  }

  /**
   * Gets the Help Content to Display By Route
   *
   * @param route: Route to get content for
   * @returns the help content
   */
  getHelpContentByRoute (route: PageHelpRoutes) {
    switch (route) {
      case '/management/manage-applications/applications':
        return this.getApplicationsHelpContent();

        case '/management/manage-nominations/nominations':
          return this.getNominationsHelpContent();

        case '/management/reporting':
          return this.getReportingHelpContent();

        case '/management/payment-processing/available':
          return this.getPaymentProcessingHelpContent();

        case '/management/program-setup/programs/all':
          return this.getGrantProgramsHelpContent();

        case '/management/program-setup/nomination-programs/all':
          return this.getNominationProgramsHelpContent();

        case '/management/program-setup/budgets/open':
          return this.getBudgetsHelpContent();

        case '/management/program-setup/forms/all':
          return this.getCustomFormsHelpContent();

        case '/management/program-setup/funding-sources/open':
          return this.getFundingSourcesHelpContent();

        case '/management/program-setup/workflows':
          return this.getWorkflowsHelpContent();

        case '/management/configure/system-emails':
          return this.getSystemEmailsHelpContent();
    }

    return null;
  }

  /**
   * Get the Application Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Applications Area
   */
  getApplicationsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUse',
      {},
      'Use the Applications area to manage applications submitted for your grant programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textApplicationHelpBullet1',
      {},
      'Route applications to specific workflow levels manually or using automation'
    );
    const bullet2 = this.i18n.translate(
      'common:textApplicationHelpBullet2',
      {},
      'Act on applications, including Approve, Decline, and Cancel'
    );
    const bullet3 = this.i18n.translate(
      'common:textApplicationHelpBullet3',
      {},
      'Add internal and external communications to the Application View'
    );
    const bullet4 = this.i18n.translate(
      'common:textApplicationHelpBullet4',
      {},
      'Download applications, including the Activity Trail'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithApplicationsGuide',
      {},
      'Explore the "Get Started with Applications" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithApplications',
        {},
        'Get Started with Applications'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${ApplicationsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${ApplicationsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Nomination Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Nominations Area
   */
   getNominationsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseNominations',
      {},
      'Use the Nominations area to create, manage, and act on nominations submitted for your nomination programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textNominationHelpBullet1',
      {},
      'Route nominations to specific workflow levels manually or using automation'
    );
    const bullet2 = this.i18n.translate(
      'common:textNominationHelpBullet2',
      {},
      'Act on nominations, including Approve, Decline, and Cancel'
    );
    const bullet3 = this.i18n.translate(
      'common:textNominationHelpBullet3',
      {},
      'Add internal communications to the Nomination View'
    );
    const bullet4 = this.i18n.translate(
      'common:textNominationHelpBullet4',
      {},
      'Download nominations, including the Activity Trail'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithNominationsGuide',
      {},
      'Explore the "Get Started with Nominations" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithNominations',
        {},
        'Get Started with Nominations'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${NominationsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${NominationsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Reporting Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Reporting Area
   */
   getReportingHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseReporting',
      {},
      'Use the Ad Hoc area to report on a program\'s assets like applications, budgets, payments, and more. Reports can be generated whenever they are needed and shared with others in your organization.'
    );
    const bullet1 = this.i18n.translate(
      'common:textReportingHelpBullet1',
      {},
      'Create reports for areas across GrantsConnect'
    );
    const bullet2 = this.i18n.translate(
      'common:textReportingHelpBullet2',
      {},
      'Build reports from standard report templates'
    );
    const bullet3 = this.i18n.translate(
      'common:textReportingHelpBullet3',
      {},
      'Share reports with others in your organization'
    );
    const bullet4 = this.i18n.translate(
      'common:textReportingHelpBullet4',
      {},
      'Schedule reports to be sent daily, weekly, or monthly'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithReportingGuide',
      {},
      'Explore the "Get Started with Ad Hoc Reporting" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithReporting',
        {},
        'Get Started with Ad Hoc Reporting'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${ReportingHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${ReportingHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Payment Processing Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Payment Processing Area
   */
   getPaymentProcessingHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUsePaymentProcessing',
      {},
      'Use the Payment Processing area to create and disburse payments to fulfill cash and in-kind awards.'
    );
    const bullet1 = this.i18n.translate(
      'common:textPaymentProcessingHelpBullet1',
      {},
      'Create awards with cash or in-kind payments'
    );
    const bullet2 = this.i18n.translate(
      'common:textPaymentProcessingHelpBullet2',
      {},
      'Option for the client or YourCause to process cash payments'
    );
    const bullet3 = this.i18n.translate(
      'common:textPaymentProcessingHelpBullet3',
      {},
      'Group payments into manageable batches'
    );
    const bullet4 = this.i18n.translate(
      'common:textPaymentProcessingHelpBullet4',
      {},
      'Integrate payments with your Accounts Payable (AP) system'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithPaymentProcessingGuide',
      {},
      'Explore the "Get Started with Payment Processing" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithPaymentProcessing',
        {},
        'Get Started with Payment Processing'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${PaymentProcessingHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${PaymentProcessingHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Grant Programs Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Grant Programs Area
   */
   getGrantProgramsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseGrantPrograms',
      {},
      'Use the Grant Programs area to create and manage your grant programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textGrantProgramsHelpBullet1',
      {},
      'Use program cycles to control when Applicants can apply for grants'
    );
    const bullet2 = this.i18n.translate(
      'common:textProgramsHelpBullet2',
      {},
      'Establish workflows to determine how applications flow within the program'
    );
    const bullet3 = this.i18n.translate(
      'common:textGrantProgramsHelpBullet3',
      {},
      'Determine if an Applicant meets your program requirements by completing an Eligibility Form'
    );
    const bullet4 = this.i18n.translate(
      'common:textGrantProgramsHelpBullet4',
      {},
      'Route Applicants to specific grant programs using Program Automation'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithGrantProgramsGuide',
      {},
      'Explore the "Get Started with Grant Programs" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithGrantPrograms',
        {},
        'Get Started with Grant Programs'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${GrantProgramsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${GrantProgramsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Nomination Programs Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Nomination Programs Area
   */
   getNominationProgramsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseNominationProgramsHelpContent',
      {},
      'Use the Nomination Programs area to create and manage your nomination programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textNominationProgramsHelpBullet1HelpContent',
      {},
      'Use program cycles to control when Nominators can submit applications'
    );
    const bullet2 = this.i18n.translate(
      'common:textProgramsHelpBullet2',
      {},
      'Establish workflows to determine how applications flow within the program'
    );
    const bullet3 = this.i18n.translate(
      'common:textNominationProgramsHelpBullet3HelpContent',
      {},
      'Specify which grant programs Nominees will be routed to once approved'
    );
    const bullet4 = this.i18n.translate(
      'common:textNominationProgramsHelpBullet4HelpContent',
      {},
      'Customize the system emails sent as part of the nomination process'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithNominationProgramsGuideHelpContent',
      {},
      'Explore the "Get Started with Nomination Programs" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithNominationProgramsHelpContent',
        {},
        'Get Started with Nomination Programs'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${NominationProgramsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${NominationProgramsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Budgets Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Budgets Area
   */
   getBudgetsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseBudgets',
      {},
      'Use the Budgets area to create meaningful budgets to fund awards for your grant programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textBudgetsHelpBullet1',
      {},
      'Create budgets for cash or in-kind donations'
    );
    const bullet2 = this.i18n.translate(
      'common:textBudgetsHelpBullet2',
      {},
      'Import budgets in bulk'
    );
    const bullet3 = this.i18n.translate(
      'common:textBudgetsHelpBullet3',
      {},
      'Specify the amount of money available to your programs'
    );
    const bullet4 = this.i18n.translate(
      'common:textBudgetsHelpBullet4',
      {},
      'Establish budgets that can be used across programs'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithBudgetsGuide',
      {},
      'Explore the "Get Started with Budgets" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithBudgets',
        {},
        'Get Started with Budgets'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${BudgetsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${BudgetsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Forms Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Forms Area
   */
   getCustomFormsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseCustomForms',
      {},
      'Use the Custom Forms area to build forms for grant and nomination programs, including forms submitted by Applicants and used by Grant Managers to review applications.'
    );
    const bullet1 = this.i18n.translate(
      'common:textCustomFormsHelpBullet1',
      {},
      'Create forms to be used for applications, reviews, and program routing'
    );
    const bullet2 = this.i18n.translate(
      'common:textCustomFormsHelpBullet2',
      {},
      'Provide translations for forms to better support a global audience'
    );
    const bullet3 = this.i18n.translate(
      'common:textCustomFormsHelpBullet3',
      {},
      'Configure forms with conditional logic and validation options'
    );
    const bullet4 = this.i18n.translate(
      'common:textCustomFormsHelpBullet4',
      {},
      'Require Applicants to provide an electronic signature when submitting a form'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithCustomFormsGuide',
      {},
      'Explore the "Get Started with Custom Forms" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithCustomForms',
        {},
        'Get Started with Custom Forms'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${CustomFormsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${CustomFormsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Funding Sources Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Funding Sources Area
   */
   getFundingSourcesHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseFundingSources',
      {},
      'Use the Funding Sources area to create buckets of funds for your budgets.'
    );
    const bullet1 = this.i18n.translate(
      'common:textFundingSourcesHelpBullet1',
      {},
      'Create funding sources for cash or in-kind donations'
    );
    const bullet2 = this.i18n.translate(
      'common:textFundingSourcesHelpBullet2',
      {},
      'Specify the amount of money avaliable to your budgets'
    );
    const bullet3 = this.i18n.translate(
      'common:textFundingSourcesHelpBullet3',
      {},
      'Establish funding sources that can be used across programs'
    );
    const bullet4 = this.i18n.translate(
      'common:textFundingSourcesHelpBullet4',
      {},
      'Estimate the cash amount of in-kind items with the funding source calculator'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithFundingSourcesGuide',
      {},
      'Explore the "Get Started with Funding Sources" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithFundingSources',
        {},
        'Get Started with Funding Sources'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${FundingSourcesHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${FundingSourcesHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the Workflows Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for Workflows Area
   */
   getWorkflowsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseWorkflows',
      {},
      'Use the Workflows area to define the routes applications will take as they move through grant and nomination programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textWorkflowsHelpBullet1',
      {},
      'Customize the structure of workflows using levels and sub-levels'
    );
    const bullet2 = this.i18n.translate(
      'common:textWorkflowsHelpBullet2',
      {},
      'Specify the actions that can be taken at each workflow level and sub-level'
    );
    const bullet3 = this.i18n.translate(
      'common:textWorkflowsHelpBullet3',
      {},
      'Use Workflow Level Permissions and Workflow Manager Permissions to determine who can view and manage applications'
    );
    const bullet4 = this.i18n.translate(
      'common:textWorkflowsHelpBullet4',
      {},
      'Automatically route applications to workflow levels based on custom criteria'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithWorkflowsGuide',
      {},
      'Explore the "Get Started with Workflows" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithWorkflows',
        {},
        'Get Started with Workflows'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${WorkflowsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${WorkflowsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }

  /**
   * Get the System Emails Help Content to be displayed in the modal
   *
   * @returns Help Content to Display for System Emails Area
   */
   getSystemEmailsHelpContent () {
    const firstLine = this.i18n.translate(
      'common:textUseSystemEmails',
      {},
      'Use the System Emails area to review and customize email templates used in your grant and nomination programs.'
    );
    const bullet1 = this.i18n.translate(
      'common:textSystemEmailsHelpBullet1',
      {},
      'Create custom copies of system emails specific to your organization, programs, and processes'
    );
    const bullet2 = this.i18n.translate(
      'common:textSystemEmailsHelpBullet2',
      {},
      'Preview or send test emails before making them live'
    );
    const bullet3 = this.i18n.translate(
      'common:textSystemEmailsHelpBullet3',
      {},
      'Provide translations for emails to support a global audience'
    );
    const bullet4 = this.i18n.translate(
      'common:textSystemEmailsHelpBullet4',
      {},
      'Take advantage of 80+ email templates intended for administration, applications, invitations, reporting, and more'
    );
    const bulletLink1 = this.i18n.translate(
      'common:textExploreGetStartedWithSystemEmailsGuide',
      {},
      'Explore the "Get Started with System Emails" guide'
    );

    return {
      modalHeader: this.i18n.translate(
        'common:hdrGetStartedWithSystemEmails',
        {},
        'Get Started with System Emails'
      ),
      modalContent: `<div>
        <div class="font-italic mb-3">${firstLine}</div>
        <div class="font-550 font-1-rem mb-2">${this.keyFeaturesText}</div>
        <ul>
          <li>${bullet1}</li>
          <li>${bullet2}</li>
          <li>${bullet3}</li>
          <li>${bullet4}</li>
        </ul>
        <div class="font-550 font-1-rem mt-3 mb-2">${this.learnMoreText}</div>
        <div class="font-italic mb-2">${this.findOutMoreText}</div>
        <ul>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${SystemEmailsHelpLink1}">
              ${bulletLink1}
            </a>
          </li>
          <li>
            <a class="anchor-tag"
              target="_blank"
              href="${SystemEmailsHelpLink2}">
              ${this.viewAllResources}
            </a>
          </li>
        </ul>
      </div>`
    };
  }
}
